/**
 * @file sync/init.js
 * @description Wires SyncCoordinator to the audio engine after initialization.
 * Called from createInitializeBaseFlow.js once the audio engine is registered on the active voice.
 *
 * Tier selection:
 *   ?room=<name>  →  Tier 2: WebSocketSyncAdapter (Connect server, global)
 *   (no param)    →  Tier 1: BroadcastChannelAdapter (same browser only)
 */

import { syncCoordinator } from './SyncCoordinator.js';
import { BroadcastChannelAdapter } from './adapters/BroadcastChannelAdapter.js';
import { WebSocketSyncAdapter } from './adapters/WebSocketSyncAdapter.js';
// Resolver moved to the shared pkg (local trackSyncMetadata no longer exports it).
import { resolveTrackBpmFromTrackData } from 'entangled-worlds-orbiters-shared/clock/track-metadata';
import { getLaunchGridFromUrl, getMultiOrbiterModeFromUrl, getRosterFromUrl, getRoomIdFromUrl, getRoomCreateFromUrl, getRoomPlayersFromUrl } from '../utils/urlParams.js';
import { getRoomGrantId } from '../room/roomGrant.js';
import { getRoomInviteUrl } from '../room/roomInvite.js';
import { getRoomRoster } from '../room/roomRoster.js';
import { getRoomClientId, getRoomDisplayName, getRoomReclaimToken, resolveDisplayName, ensureRoomIdentity } from '../room/identity.js';
import { getAuthRoomIdentity, resolveAuthRoomIdentity, onAuthRoomIdentityUpgrade } from '../room/authIdentity.js';
import { initRoomClient, disposeRoomClient } from '../room/roomClient.js';
import { getLaunchGridBars, getLaunchGridQuarterBeats, setLaunchGridBeats } from './launchGrid.js';
import { deckFor } from '../voice/Deck.js';
import { createLocalPulseClock } from './pulseClock.js';
import { initSharedClockPulse } from './sharedClock.js';
import { voiceRegistry } from '../voice/VoiceRegistry.js';

const WS_URL = import.meta.env?.VITE_WS_CONNECT ?? 'wss://connect.plantasia.space/ws/';

// The realm-wide shared-clock handle (decision 005: no window globals as seams — this replaces
// `window.__orbitersSharedClock`). A plain module singleton: any importer gets the SAME live handle
// regardless of whether ITS OWN initSync ever ran (a multi-orbiter audio-only voice reads the primary
// voice's pulse this way). `{ getState, dispose } | null`.
let sharedClockHandle = null;

/** The current shared-clock snapshot (`{ joined, beatNow, phaseNow, tempoBpm, quantum } | null`), or
 *  null when no pulse is wired yet. The one read seam for `window.__orbitersSharedClock.getState()`. */
export function getSharedClockState() {
  return sharedClockHandle?.getState?.() ?? null;
}

// A multi-orbiter tab boots N voices, each calling initSync — but a tab is ONE Connect peer, so the
// room SOCKET + shared-clock PULSE are built ONCE and reused by the sibling voices. (A fresh adapter
// per voice made every voice its own peer and left the singleton coordinator — and therefore the SYNC
// badge — reading a never-connected adapter, so remote voices were ignored.) The coordinator is still
// re-init'd per voice with that one adapter+pulse (idempotent: #wirePulse unsubscribes first), so each
// voice seeds its own trackBpm/tempo exactly as before. Keyed by room so a room change rebuilds.
// Single-orbiter never caches (one voice → always builds fresh), so its path stays byte-identical.
let sharedRoom = null; // { room, adapter, pulse } | null

/**
 * The tab's one room socket, with identity read fresh on every (re)register (getter-backed).
 * @returns {{ adapter: WebSocketSyncAdapter, identityReady: Promise<string> }} `identityReady`
 *   settles once this tab's clientId is FINAL — the room client waits on it before reading it.
 *   Deliberately NOT the register's full wait: that also includes the bounded account lookup (a
 *   network round-trip), and room mode must not be dark for that long.
 */
function buildRoomAdapter(room) {
  const identityReady = ensureRoomIdentity(room);
  const adapter = new WebSocketSyncAdapter(WS_URL, room, {
    get clientId() { return getRoomClientId(); },
    // Name precedence: the user's explicit persisted choice, else the logged-in account's
    // display name / username, else the generated guest handle (resolveDisplayName's fallback).
    get displayName() {
      return getRoomDisplayName() ?? getAuthRoomIdentity()?.displayName ?? resolveDisplayName();
    },
    get userId() { return getAuthRoomIdentity()?.userId ?? null; },
    get reclaimToken() { return getRoomReclaimToken(); },
    // The FIRST register waits (bounded — falls back to guest) for the account resolution kicked
    // off here, instead of racing it; reconnect registers reuse the frozen outcome instantly.
    // It also waits for the room identity restore: a browser restart boots with an empty
    // sessionStorage, and the room's localStorage identity copy must be adopted (or ruled out —
    // another live tab may hold it) BEFORE the register reads the clientId/reclaimToken getters.
    whenReady: Promise.allSettled([resolveAuthRoomIdentity(), identityReady]),
  });
  // A cold auth stack can outlive the bounded first-register wait, sending the register with the
  // generated guest handle. When the account lands late (guest → account, at most once), one
  // re-register presents the real name and the relay re-runs its name assignment — otherwise a
  // logged-in member would keep the stale handle for the whole session.
  onAuthRoomIdentityUpgrade(() => adapter.resendRegister());
  return { adapter, identityReady };
}

/**
 * Tear down the tab's previous room socket + pulse before a different room (or no room at all)
 * replaces it. An orphaned adapter keeps its auto-reconnect timer and keeps registering under the
 * same clientId, so the tab would run as a SECOND relay peer and the old room would still count it
 * as present, holding its slots against the grace window. The room client rides that socket's raw
 * tee, so it goes with it.
 */
function releaseSharedRoom() {
  disposeRoomClient(); // a client outliving its socket keeps the realm locked to a dead room
  if (!sharedRoom) return;
  const { adapter, pulse } = sharedRoom;
  sharedRoom = null;
  if (pulse) {
    try { pulse.dispose?.(); } catch { /* already gone */ }
    sharedClockHandle = null; // wraps that same pulse
  }
  try { adapter?.destroy?.(); } catch { /* already gone */ }
}

/** The Connect-tee shared-clock pulse (rides the one room socket); adopts the room tempo, never seeds. */
function buildRoomPulse(adapter, room, audioEngine) {
  return initSharedClockPulse(adapter, {
    sessionId: room,
    audioEngine,
    // First client in the room establishes the session at the enabling deck's tempo.
    onFirstInRoom: () => {
      const enabler = voiceRegistry.all().find((v) => v?.deck?.syncEnabled === true)?.deck;
      const tempo = enabler?.tempo ?? syncCoordinator.bpm;
      if (Number.isFinite(tempo) && tempo > 0) {
        syncCoordinator.setTempo(tempo, { sourceType: 'manual' });
      }
    },
  });
}

/**
 * Arm the tab's ONE room client on the shared socket. Wire voice ids are positional stage indices.
 *  - A collection room boot carries the fixed DECK count + a (possibly empty) live stage→voice map via
 *    the room-roster seam. Decks start empty, so the order can be empty here — the deck count is the
 *    mint shape, and the order is republished as decks load. Arm even with an empty order.
 *  - An inline-roster multi boot carries its order in the URL; its voices ARE its decks (no separate
 *    deck count), so `voiceCount` is null (the client falls back to the mapped count).
 * `?create=1` (creator's tab) mints before joining; share links drop the flag → join only.
 */
/**
 * The fewest seats a room is ever built with. Used ONLY as the last-resort floor when neither the
 * grant nor the link states a seat count — a mint with no seat count is refused by the relay, so a
 * silent backend would otherwise mean no rooms at all rather than a room of the usual size.
 */
const MIN_ROOM_SEATS = 2;

function armRoomClient(adapter, room, roomRoster, identityReady) {
  let voiceOrder;
  let voiceCount;
  if (roomRoster) {
    voiceOrder = Array.isArray(roomRoster.voiceOrder) ? roomRoster.voiceOrder : [];
    voiceCount = roomRoster.deckCount;
  } else {
    const inlineRoster = getRosterFromUrl();
    if (!Array.isArray(inlineRoster) || inlineRoster.length === 0) return;
    voiceOrder = inlineRoster.map((entry) => entry.voiceId);
    voiceCount = null;
  }
  // The seat count comes from the room's own record (the grant, via the collection seam), not from
  // the link — the link is editable and no longer carries it at all. `?players=` survives only as
  // the fallback for an older link, and for an inline-roster boot that has no grant to read.
  //
  // The final floor is what makes a MINT survive a backend that cannot answer yet. These apps
  // deploy independently, so there is a window where this build is live and the collection read
  // does not return the room's shape: grant and link would both be silent, we would send
  // `players: null`, and the relay refuses that outright — nobody could open a room at all. A room
  // is never built for fewer than this, so the floor cannot invent a shape that is wrong.
  const create = getRoomCreateFromUrl()
    ? {
        players: roomRoster?.players ?? getRoomPlayersFromUrl() ?? MIN_ROOM_SEATS,
        grantId: getRoomGrantId(),
        rosterUrl: getRoomInviteUrl(),
        score: roomRoster?.score ?? null,
        // The card draw and the pool it deals from, stated once by the creator. Nothing derives
        // them from the link: like the score, they were recorded on the grant at mint.
        draw: roomRoster?.draw ?? null,
        pinned: roomRoster?.pinned ?? null,
      }
    : null;
  // Build on the SETTLED identity. The restore is part of what the socket's first register waits on,
  // so the client always exists before anything goes on the wire. Constructing it earlier would read
  // a sessionStorage the restart-restore hasn't written yet: the client would MINT a throwaway
  // clientId and answer every ownership check against an id it never registered with.
  void identityReady.then(() => {
    if (sharedRoom?.adapter !== adapter) return; // the tab moved on (room change / teardown)
    initRoomClient({ adapter, roomId: room, voiceOrder, voiceCount, create });
  });
}

/**
 * Arm the room presence (socket + room client) WITHOUT waiting for a voice — the collection app calls
 * this at boot so a room gathers with EMPTY decks (no voice boots until a player deals a card). The
 * shared-clock pulse is built lazily by the first voice's `initSync` (it needs an audio engine). A
 * sibling that already built the socket makes this a no-op.
 */
export function ensureRoomClientArmed() {
  const room = getRoomIdFromUrl();
  if (!room) return;
  const roomRoster = getRoomRoster();
  const roomRealm = getMultiOrbiterModeFromUrl() === 'multi' || Boolean(roomRoster);
  if (!roomRealm || sharedRoom?.room === room) return;
  releaseSharedRoom(); // a stale socket for a DIFFERENT room must not outlive this one
  const { adapter, identityReady } = buildRoomAdapter(room);
  sharedRoom = { room, adapter, pulse: null };
  armRoomClient(adapter, room, roomRoster, identityReady);
}

/**
 * Resolve the adapter + shared-clock pulse for THIS voice's `initSync`, reusing the tab's one room
 * socket where one exists and building the pulse lazily.
 *  - Reuse: a sibling voice (or the collection boot's `ensureRoomClientArmed`) already built the socket
 *    for this room. Reuse it; build the pulse now if it wasn't built yet (the boot-time arm has no
 *    audio engine, so the first voice with one seeds the pulse). Room pulses never seed (they adopt).
 *  - Fresh: single-orbiter, in-tab multi, or the first voice of a room realm. Build the adapter, the
 *    matching pulse, and — for a room realm — arm the room client on the new socket.
 */
function ensureSyncInfra({ room, roomRealm, roomRoster, audioEngine }) {
  if (room && roomRealm && sharedRoom?.room === room) {
    const adapter = sharedRoom.adapter;
    if (!sharedRoom.pulse && audioEngine) {
      sharedRoom.pulse = buildRoomPulse(adapter, room, audioEngine);
      if (sharedRoom.pulse) {
        sharedClockHandle = { getState: sharedRoom.pulse.getState, dispose: sharedRoom.pulse.dispose };
      }
    }
    return { adapter, pulse: sharedRoom.pulse, seedPulse: false };
  }

  // Release the previous room socket + client before building anything: this path is only reached
  // when the tab is moving to a DIFFERENT room, or out of room mode entirely.
  releaseSharedRoom();
  const built = room ? buildRoomAdapter(room) : null;
  const adapter = built?.adapter ?? new BroadcastChannelAdapter();
  // Dispose any prior handle first (re-init / track switch); clear before building so a re-init that
  // builds NO pulse never leaves the audio path reading a disposed handle.
  try { sharedClockHandle?.dispose?.(); } catch { /* already gone */ }
  sharedClockHandle = null;
  let pulse = null;
  let seedPulse = false;
  if (room && adapter instanceof WebSocketSyncAdapter) {
    // ROOM: the Connect-tee pulse rides the one ?room socket; a joiner ADOPTS the room tempo (no seed).
    pulse = buildRoomPulse(adapter, room, audioEngine);
  } else if (!room) {
    // IN-TAB: a shared session is live once ≥2 voices coexist AND sync is enabled. Solo → null → never
    // quantizes → byte-identical. Seeds the pulse (it's the tempo authority for the in-tab feed).
    pulse = createLocalPulseClock({
      isJoined: () => syncCoordinator.isEnabled && voiceRegistry.size >= 2,
      // The launch grid is per-deck; the pulse's quantum reports the FOCUSED deck's grid (the realm
      // boot value is only the fallback before any deck exists).
      getQuantum: () => deckFor(null)?.launchGridQuarterBeats ?? getLaunchGridQuarterBeats(),
    });
    seedPulse = true;
  }
  if (pulse) sharedClockHandle = { getState: pulse.getState, dispose: pulse.dispose };
  // Remember a room-realm build so sibling voices reuse this one socket + pulse.
  sharedRoom = (room && roomRealm) ? { room, adapter, pulse } : null;

  // A multi realm opened WITH a room is a multiplayer session: arm the room client on this same socket
  // (one tab = one relay peer). Guarded on the tee so a presence-only adapter double stays inert.
  // A rebuild WITHOUT a room needs nothing here — releaseSharedRoom() above already dropped the old
  // client, whose gates/covers/tee subscription would otherwise keep the realm locked to a dead room.
  if (sharedRoom && built && typeof adapter.onRawMessage === 'function') {
    armRoomClient(adapter, room, roomRoster, built.identityReady);
  }
  return { adapter, pulse, seedPulse };
}

/**
 * @param {import('../audio/AudioEngineAdapter.js').AudioEngineAdapter} audioEngine
 * @param {object|null} trackData — full track release response, may contain audioAnalysis
 * @param {import('../core/ParameterManager.js').ParameterManager|null} parameterManager
 */
export function initSync(audioEngine, trackData, parameterManager = null) {
  if (!audioEngine?.transport) {
    console.warn('[Sync] initSync called before audio engine transport is ready.');
    return;
  }

  const trackBpm = resolveTrackBpmFromTrackData(trackData);
  const room = getRoomIdFromUrl();
  const multi = getMultiOrbiterModeFromUrl() === 'multi';
  // A collection boot carries no inline roster, so `multi` is false — but a collection opened WITH a
  // room is still a multiplayer realm. Its positional voice order arrives via the room-roster seam (the
  // collection app publishes it before any voice boots). Treat an inline multi OR a collection room boot
  // as a room-capable multi realm: one shared socket + pulse for the whole tab, and one room client.
  const roomRoster = room ? getRoomRoster() : null;
  const roomRealm = multi || Boolean(roomRoster);

  const { adapter, pulse, seedPulse } = ensureSyncInfra({ room, roomRealm, roomRoster, audioEngine });

  // Wire the (singleton) coordinator to that one adapter+pulse — EVERY voice, so each seeds its own
  // trackBpm/tempo (last-writer-wins, unchanged). Idempotent on reuse: #wirePulse unsubscribes first.
  // The master tempo starts at the transport "BPM" number (the master clock), not the track's native BPM.
  const numberBpm = Number(parameterManager?.getRawValue?.('sync-bpm'));
  syncCoordinator.init({
    adapter,
    trackBpm,
    masterBpm: Number.isFinite(numberBpm) && numberBpm > 0 ? numberBpm : null,
    pulse,
    seedPulse,
    isRoom: Boolean(room), // B2: only a room adapter's (live, server-scoped) peers feed the SYNC badge
  });

  // Sync is ON by default. Decks are seeded from trackData while the audio engine is BUILT — before
  // this runs — so the primary voice's own seed found no coordinator to enable and deferred. Now that
  // the coordinator has its adapter, sweep the registered decks and let each apply its default (the
  // deck owns the rule and the one-shot; this is only the "it's safe now" trigger). A voice created
  // after boot needs nothing here — the coordinator is already live when its seed runs.
  voiceRegistry.all().forEach((voice) => voice?.deck?.applyDefaultSync?.());

  if (parameterManager?.setRawValue && Number.isFinite(trackBpm) && trackBpm > 0) {
    try {
      parameterManager.setRawValue('sync-track-bpm', trackBpm);
    } catch {}
  }

  // No `window.__orbitersSync` alias — readers import the `syncCoordinator` singleton
  // directly (it's the one session coordinator). The shared clock stays realm-global (see
  // `getSharedClockState` above), same reasoning.

  // Launch-quantize grid (beats per launch boundary). A session can pin it with ?launchGrid=<n>.
  // The grid is PER-DECK now — the module value is the seed for decks constructed later, and any
  // deck already registered (this voice's own, constructed at registration) adopts the pin here.
  const urlGrid = getLaunchGridFromUrl();
  if (urlGrid != null) {
    setLaunchGridBeats(urlGrid);
    // A URL pin is boot configuration, not a user pick on a live deck: `roomRemote` so it applies
    // without publishing (in a room the grid is shared state, and each client seeds its own).
    voiceRegistry.all().forEach((voice) => voice?.deck?.setLaunchGridBars(getLaunchGridBars(), { roomRemote: true }));
  }
}
